/**
 * @deprecated Use PredictiveBundleController and PredictiveBundleService directly instead.
 */

import type { Dict } from '../../types';
import type { Context } from '../../types/context';
import { getAppModuleRef } from '../app/app.module';
import type { PredictiveBundleData } from './predictive-bundle.model';
import type { PredictiveBundleModule } from './predictive-bundle.module';

// Re-export BundleEventProvider from the event file for backward compatibility
export { BundleEventProvider } from '../../@deprecation/dynamicBundle/event';

// Session storage key - must match legacy value for analytics compatibility
export const KEY_DYNAMIC_BUNDLE_DATA_SESSION = 'DynamicBundleDataSession';

let predictiveBundleModule: PredictiveBundleModule;

async function importPredictiveBundleModule() {
  if (predictiveBundleModule) {
    return predictiveBundleModule;
  }

  predictiveBundleModule = await getAppModuleRef().loadModule<PredictiveBundleModule>(
    () =>
      import(
        /* webpackChunkName: "predictive-bundle.module" */
        '../predictive-bundle/predictive-bundle.module'
      ).then((m) => m.PredictiveBundleModule),
    { singleton: true }
  );

  return predictiveBundleModule;
}

export const initDynamicBundle = async (_context: Context): Promise<void> => {
  // New module auto-initializes via TAE block listener in predictive-bundle.module.ts
  // This is a no-op for backward compatibility
  await importPredictiveBundleModule();
};

export const handleClickEmbeddedBundle = async (bundle: PredictiveBundleData): Promise<void> => {
  const module = await importPredictiveBundleModule();
  const controller = module?.getController();

  controller?.openPredictiveBundleModal(bundle.widgetId, 'instantSearch', {
    predictiveBundleDataResolver: () => bundle,
  });
};

export const renderRecommendationForSearchPage = async (_context: Context): Promise<void> => {
  // New module handles rendering via TAE blocks
  // This is a no-op for backward compatibility
  await importPredictiveBundleModule();
};

export const renderPredictiveBlocksForProductPage = async (
  _context: Context,
  productId: string | number
): Promise<void> => {
  const module = await importPredictiveBundleModule();

  if (module) {
    await module.renderBlocksForProductPage(productId, 'bc-predictive-bundle');
  }
};

export const dynamicBundleData = async (
  _context: Context,
  bundle: Dict,
  _optionData: Dict,
  action: string,
  _target: Element
): Promise<Dict> => {
  const module = await importPredictiveBundleModule();

  if (!module) {
    return bundle;
  }

  const service = module.service;

  if (!service) {
    return bundle;
  }

  const bundleAction =
    action === 'init-data'
      ? 'init-data'
      : action === 'change-variant'
        ? 'change-variant'
        : action === 'click-checkbox'
          ? 'click-checkbox'
          : 'init-data';

  const result = service.calculateBundleData(bundle as any, {}, bundleAction as any);

  return result as unknown as Dict;
};

export const getDynamicBundleSettingsByWidgetId = async (
  _context: Context,
  widgetId: string
): Promise<Dict> => {
  const module = await importPredictiveBundleModule();

  if (!module || !widgetId) {
    return {};
  }

  const service = module.service;

  if (!service) {
    return {};
  }

  const settings = service.getWidgetSettings(widgetId);
  return settings;
};

export const isValidateDynamicBundle = async (context: Context): Promise<boolean> => {
  const module = await importPredictiveBundleModule();

  if (!module) {
    return false;
  }

  const service = module.service;

  if (!service) {
    return false;
  }

  return service.isValidBundle(context.widgetId);
};

export const formatPrice = async (context: Context, price: number): Promise<string> => {
  const module = await importPredictiveBundleModule();

  if (!module) {
    return String(price);
  }

  const service = module.service;

  if (!service) {
    return String(price);
  }

  const showCentAsSuperscript =
    context.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price
      ?.showCentAsSuperscript ?? false;
  const showCurrencyCodes =
    context.templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price
      ?.showCurrencyCodes ?? false;
  const removeDecimalPoint = showCentAsSuperscript;

  return service.formatPrice({
    value: Number(price),
    showCurrencyCodes,
    showCentAsSuperscript,
    removeDecimalPoint,
  });
};

export const focusOnSearchElements = (): void => {
  // Direct DOM manipulation - replicate controller logic
  const searchInput = document.querySelector('.boost-sd__search-form-input');
  const initInput = document.querySelector(
    '.boost-sd__search-widget-init-input[aria-expanded="true"]'
  );
  const searchIcon = document.querySelector(`
    .logo-area__right .show-search-link,
    .header__icon--search,
    .header__icon-wrapper,
    .js-search-sidebar-link,
    .js-search-header,
    .header__secondary-nav a[href="/search"],
    .site-header__links .search-button,
    .header-navs .js-search-trigger,
    .header--search,
    .navlink--search,
    .js-modal-open-search-modal,
    .slideout__trigger-search
  `);

  if (searchInput instanceof HTMLElement) {
    searchInput.focus();
  } else if (initInput instanceof HTMLElement) {
    initInput.focus();
  } else if (searchIcon instanceof HTMLElement) {
    searchIcon.focus();
  } else {
    window.scrollTo({ top: 10, behavior: 'smooth' });
    const skipToContentLink = Array.from(document.querySelectorAll('a')).find((link) =>
      link.textContent?.includes('Skip to content')
    );
    if (skipToContentLink) {
      skipToContentLink.focus();
    }
  }
};

export const trapFocusInBundle = (
  bundleWrapper: Element,
  _context: Context,
  isModal: boolean
): void => {
  if (!(bundleWrapper instanceof HTMLElement)) return;

  const focusableElements = bundleWrapper.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  const firstFocusableElement = focusableElements[0] as HTMLElement;
  const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

  bundleWrapper.addEventListener('keydown', (e) => {
    const isTabPressed = e.key === 'Tab';
    if (isModal && isTabPressed) {
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement?.focus();
          e.preventDefault();
        }
      }
    }
  });
};
